import withStyles from 'isomorphic-style-loader/withStyles';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cx from 'classnames';
import _ from 'lodash';
import { injectIntl, FormattedHTMLMessage, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { isWidthDown } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import withWidth from '@material-ui/core/withWidth';
import VideoPlayer from '../../../components/VideoPlayer/VideoPlayer';
import { getStreamChatUrl, isIOS } from '../../../helpers';
import isLoggedIn from '../../../helpers/checkAuth';
import LivestreamBadge from '../LivestreamBadge/LivestreamBadge';

import s from './CourseLivestream.scss';
import messages from './courseLivestreamMessages';

@injectIntl
@withWidth()
@withStyles(s)
@connect(state => ({
  auth: state.auth,
  course: state.courseDetails.course,
  isLiveStreamChatShown: state.courseDetails.isLiveStreamChatShown,
  lang: state.intl.locale,
}))
export default class CourseLivestream extends React.Component {
  static propTypes = {
    auth: PropTypes.shape({
      currentTime: PropTypes.string.isRequired,
    }).isRequired,
    course: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      liveStreamStart: PropTypes.string.isRequired,
      liveStreamEnd: PropTypes.string.isRequired,
      youtubeVideoUrl: PropTypes.string.isRequired,
    }).isRequired,
    lang: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    isLiveStreamChatShown: PropTypes.bool.isRequired,
    videoId: PropTypes.string.isRequired,
    videoService: PropTypes.string.isRequired,
    videoAppId: PropTypes.string,
    streamingNow: PropTypes.bool.isRequired,
    timeLeft: PropTypes.number.isRequired,
    width: PropTypes.string.isRequired,
  };

  static defaultProps = {
    videoAppId: '',
  };

  static contextTypes = {
    organizationSlug: PropTypes.string,
    whitelabeling: PropTypes.shape({}),
    cloudfrontUrl: PropTypes.string,
    fetch: PropTypes.func,
    vimeoLivestreamUrl: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      watchingSessionStart: new Date(),
      chatUrl: '',
    };

    this.timer = null;
    this.totalTime = 0;
    this.time = 0;
    this.timeStartedPlaying = 0;
    this.logVideoProgressDebounced = _.throttle(this.logVideoProgress, 5000);
  }

  async componentDidMount() {
    const { videoId, videoService, videoAppId, course } = this.props;
    const chatUrl = await getStreamChatUrl(
      course.youtubeVideoUrl,
      { videoId, videoService, videoAppId },
      this.context,
    );
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      hostName: window.location.hostname,
      chatUrl,
    });
  }

  async componentWillReceiveProps(nextProps) {
    if (this.props.videoId !== nextProps.videoId) {
      const { videoId, videoService, videoAppId, course } = nextProps;

      const chatUrl = await getStreamChatUrl(
        course.youtubeVideoUrl,
        { videoId, videoService, videoAppId },
        this.context,
      );
      this.setState({
        chatUrl,
      });
    }
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  onVideoPlayerUnmount = () => {
    if (this.player) {
      try {
        this.removeVideoPlayerEvents();
      } catch (error) {
        console.error(error);
      }
    }
  };

  onVideoPlayerReady = player => {
    player.on('play', this.startPlaying);
    player.on('pause', this.pausePlaying);
    player.on('waiting', this.pausePlaying);
    player.on('ended', this.pausePlaying);
  };

  logVideoProgress = () => {
    const { course, auth } = this.props;
    const { watchingSessionStart } = this.state;

    if (!isLoggedIn(auth)) {
      return false;
    }

    const data = {
      playedTime: Math.round(this.totalTime + this.time),
      startedPlaying: watchingSessionStart,
    };

    return this.context
      .fetch(`/api/courses/${course._id}/log-live-stream`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      .catch(console.error);
  };

  removeVideoPlayerEvents = () => {
    const { player } = this;
    player.off('pause');
    player.off('waiting');
    player.off('play');
    player.off('progress');
    player.off('ended');
  };

  startPlaying = () => {
    this.timeStartedPlaying = new Date();
    this.timer = setInterval(() => {
      this.time =
        (new Date().getTime() - this.timeStartedPlaying.getTime()) / 1000;
      this.logVideoProgressDebounced();
    }, 1000);
  };

  pausePlaying = () => {
    if (this.timer) {
      clearInterval(this.timer);
    }
    const timeStartedPlaying = this.timeStartedPlaying || new Date();
    this.totalTime +=
      (new Date().getTime() - timeStartedPlaying.getTime()) / 1000;
    this.time = 0;
  };

  render() {
    const { hostName, chatUrl } = this.state;
    const {
      lang,
      course,
      isLiveStreamChatShown,
      intl: { formatMessage },
      streamingNow,
      videoService,
      timeLeft,
      width,
    } = this.props;
    const { organizationSlug, whitelabeling } = this.context;
    const { youtubeVideoUrl } = course;

    if (timeLeft > 0) {
      const duration = moment.duration(timeLeft * 1000, 'milliseconds');
      const days = Math.floor(moment.duration(duration).asDays() || 0);
      let hours = moment.duration(duration).hours();
      let minutes = moment.duration(duration).minutes();
      let seconds = moment.duration(duration).seconds();

      hours = _.trim(hours).length === 1 ? `0${hours}` : hours;
      minutes = _.trim(minutes).length === 1 ? `0${minutes}` : minutes;
      seconds = _.trim(seconds).length === 1 ? `0${seconds}` : seconds;

      return (
        <div className={s.root}>
          <div className={s.countdown}>
            <FormattedHTMLMessage
              {...messages.livestreamStartsIn}
              values={{
                minutes,
                hours,
                seconds,
                days: days ? formatMessage(messages.days, { days }) : '',
              }}
            />
          </div>
        </div>
      );
    }

    const getVideoLoaderUrl = (whitelabeledOptions, pathTo, defaultLoader) =>
      _.get(whitelabeledOptions, `${pathTo}`)
        ? `${this.context.cloudfrontUrl}${_.get(whitelabeling, `${pathTo}`)}`
        : defaultLoader;
    const videoLoader = organizationSlug
      ? getVideoLoaderUrl(
          whitelabeling,
          'videoLoader',
          `${this.context.cloudfrontUrl}/video-loader.png`,
        )
      : `${this.context.cloudfrontUrl}/video-loader.png`;

    const thumbnail = [
      {
        srcset: videoLoader,
        type: 'image/png',
      },
    ];

    const videoJsOptions = {
      autoplay: !isIOS(),
      controls: true,
      speed: true,
      poster: thumbnail[0].srcset,
      thumbnail,
      // responsive: true,
      // fluid: true,
      language: lang,
      playsInline: isIOS(),
      controlBar: {
        children: [
          'playToggle',
          'volumePanel',
          'currentTimeDisplay',
          'progressControl',
          'qualitySelector',
          'fullscreenToggle',
        ],
      },
    };

    if (videoService === 'youtube') {
      videoJsOptions.sources = [
        {
          type: 'video/youtube',
          src: youtubeVideoUrl,
        },
      ];
      videoJsOptions.youtube = { hl: lang };
    }
    if (videoService === 'vimeo') {
      videoJsOptions.sources = [
        {
          type: 'video/vimeo',
          src: youtubeVideoUrl,
        },
      ];
      videoJsOptions.vimeo = { playsinline: !isIOS() };
    }

    return (
      <>
        {timeLeft <= 0 && isWidthDown('xs', width) && (
          <div className={cx(s.mobileTitle)}>
            <Typography variant="h1" className={s.title}>
              {course.name}
            </Typography>
            <LivestreamBadge
              timeLeft={timeLeft}
              course={course}
              streamingNow={streamingNow}
            />
          </div>
        )}
        <div className={cx(s.root, s.available)}>
          <NoSsr>
            <VideoPlayer
              onReady={this.onVideoPlayerReady}
              onUnmount={this.onVideoPlayerUnmount}
              {...videoJsOptions}
              // key={isLiveStreamChatShown}
            />
          </NoSsr>
          {isLiveStreamChatShown && hostName && (
            <NoSsr>
              <div className={s.chatWrapper}>
                <iframe
                  width="100%"
                  height="100%"
                  src={`${chatUrl}${
                    videoService === 'youtube'
                      ? `&embed_domain=${hostName}`
                      : ''
                  }`}
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  title={formatMessage(messages.livestreamChat)}
                  allowFullScreen
                />
              </div>
            </NoSsr>
          )}
        </div>
      </>
    );
  }
}
